// The view of the "Where today's trades went" card (handoff 4.3b, 4.3c): its title and spoken
// summary, its Day | Week switch, and the column headers, plot, legend and caption that layoutFlows
// works out for the body's width. The card's template binds these fields and nothing else: it calls no function and
// formats no string, so nothing is worked out again while the page sits idle.
//
// No Angular here, so Node can test it (regionFlowView.test.mjs).
import {
  flowSizing, flowsAccessibilityLabel, flowTitle, layoutFlows, OTHER, productName,
} from './regionFlows.js';

/** @typedef {import('./regionFlows.js').Flows} Flows */
/** @typedef {import('./regionFlows.js').FlowSizing} FlowSizing */

/**
 * What the region-flow-card template binds. Styles are ng-style objects, in px.
 * @typedef {object} RegionFlowView
 * @property {string} title
 * @property {{day: SpanButton, week: SpanButton}} spans The Day | Week switch.
 * @property {?string} empty In Week, while it loads or when it has no trades: the line shown instead
 *   of the chart (and of the link). ariaLabel and everything after it are then null.
 * @property {?string} ariaLabel The whole card, for a screen reader.
 * @property {?string} href
 * @property {string|undefined} linkLabel ariaLabel when the body is a link.
 * @property {?string} spoken ariaLabel when it isn't.
 * @property {?FlowSizing} sizing
 * @property {?{seller: string, buyer: string, style: object}} headers
 * @property {?{width: number, height: number, style: object, edgeWidth: number,
 *   bands: {key: string, from: string, to: string, slot: string, cls: string, path: string, edges: string}[],
 *   nodes: {key: string, x: number, y: number, width: number, height: number}[],
 *   labels: {key: string, region: string, count: string, style: object}[]}} plot
 * @property {?{style: object, items: {slot: string, cls: string, name: string, count: string}[]}} legend
 * @property {?{text: string, style: object}} caption
 */

/** @typedef {{text: string, label: string, pressed: boolean}} SpanButton */

const px = n => `${n}px`;

/** " (a day couldn't be loaded)", " (2 days couldn't be loaded)", or '' when the week is whole. */
const missingDays = n => (!n ? '' : n === 1 ? " (a day couldn't be loaded)" : ` (${n} days couldn't be loaded)`);

/** The class that fills a mark in a product's colour: main.css gives hc-p-<slot> --hc-p-<slot>. */
const slotClass = slot => `hc-p-${slot}`;

/** A legend entry's name: the slot ("PGL"), or Other with the types it stands for, busiest first. */
const bandName = band => (band.slot === OTHER
  ? `Other (${band.types.map(t => productName(t.product)).join(', ')})`
  : band.slot);

/** The small text under and over the plot (column headers, legend, caption): a size below the labels. */
const smallText = sizing => ({fontSize: px(sizing.fontSize - 1)});

/**
 * Measures a region label in the card's font: `measure(region, count, fontSize)` is the width in
 * px of the code (weight 600) followed by `count` (" 12", normal weight).
 * @callback MeasureLabel
 * @param {string} region
 * @param {string} count
 * @param {number} fontSize
 * @returns {number}
 */

/**
 * flowSizing estimates a label's box from its characters (0.62 em each, A3). A wider font than
 * the one that was made for (DejaVu Sans needs about 0.76 em a character for "MW 5") overflows the
 * box, and the ellipsis then takes the count, or the code itself ("M…"). With `measure`, a box too
 * narrow for its widest label grows to fit it, within A3's cap of 28% of the width; a box wide
 * enough stays as A3 sized it. 1 px more than the measured text covers the layout's rounding.
 * @param {FlowSizing} sizing
 * @param {Flows} flows
 * @param {number} width
 * @param {MeasureLabel} [measure]
 * @returns {FlowSizing}
 */
function fitLabels(sizing, flows, width, measure) {
  if (!measure) {
    return sizing;
  }
  const counts = flows.regions.flatMap(r => [flows.sold[r], flows.bought[r]].filter(n => n > 0).map(n => [r, n]));
  const widest = Math.max(0, ...counts.map(([region, n]) => measure(region, ` ${n}`, sizing.fontSize)));
  const fit = Math.min(Math.ceil(widest) + 1, Math.floor(width * 0.28));
  return fit > sizing.labelWidth ? {...sizing, labelWidth: fit} : sizing;
}

/**
 * The plot: the SVG's ribbons and bars, and the region labels over it, in a box the layout's size.
 * Drawn in order: each band (busiest first, so thinner bands end up on top) and then its outline,
 * and the region bars last.
 */
function plotView(layout, sizing) {
  const label = n => ({
    key: `${n.side}:${n.region}`,
    region: n.region,
    count: ` ${n.count}`,
    // Beside its bar, on one line: sellers' labels right-aligned towards their bars down the left,
    // buyers' left-aligned towards theirs down the right.
    style: {
      left: px(n.labelX),
      top: px(n.labelY),
      width: px(n.labelWidth),
      fontSize: px(sizing.fontSize),
      lineHeight: px(Math.round(sizing.fontSize * 1.3)),
      textAlign: n.side === 'seller' ? 'right' : 'left',
    },
  });
  return {
    width: layout.width,
    height: layout.height,
    style: {width: px(layout.width), height: px(layout.height)},
    edgeWidth: layout.edgeWidth,
    // Drawn in order, busiest first (thinner bands end up on top): each band, then its outline.
    bands: layout.bands.map(band => ({
      key: band.key, from: band.from, to: band.to, slot: band.slot, cls: slotClass(band.slot), path: band.path, edges: band.edges,
    })),
    nodes: layout.nodes.map(n => ({
      key: `${n.side}:${n.region}`, x: n.x, y: n.y, width: n.width, height: n.height,
    })),
    labels: layout.nodes.map(label),
  };
}

/**
 * Everything the card binds, in the card's order: the title, the Day | Week switch, then the body,
 * which is one link to today's Trades when there is an `href`.
 *
 * - `flows`: regionFlows of the trades drawn. In Day the card is only there when there are some;
 *   in Week it stays, and `flows` is null while the week loads.
 * - `day`, `previousDay`: whose trades they are, 'today', 'previous' or 'week', and what the
 *   previous report is called ("yesterday", "Friday").
 * - `span`: 'day' (the default) or 'week', the switch's state.
 * - `week`, `loading`: in Week, its range ("Sep 30 – Oct 6") and how many days couldn't be loaded,
 *   and whether its past days are still on their way.
 * - `width`: the body's content width as chart-width measures it, undefined until it has.
 * - `href`: today's Trades board, or null when the body isn't a link (no trading role).
 *
 * - `measure`: optional, measures a label in the card's font (fitLabels); without it the label
 *   boxes are A3's estimate.
 *
 * `ariaLabel` reads the whole card: as the link's label (`linkLabel`), or, when there is no link,
 * as hidden text (`spoken`); in Week it starts with the range. Until the width is measured only
 * the title and the switch show: `sizing`, `headers`, `plot`, `legend` and `caption` are null.
 *
 * @param {{flows: ?Flows, day: import('./regionFlows.js').FlowDay, previousDay: string,
 *   span?: import('./regionFlows.js').FlowSpan, week?: ?{range: string, missing: number},
 *   loading?: boolean, width: number|undefined, href: ?string, measure?: MeasureLabel}} input
 * @returns {RegionFlowView}
 */
export function regionFlowView({flows, day, previousDay, span = 'day', week, loading, width, href, measure}) {
  const weekShown = span === 'week';
  const spans = {
    day: {text: 'Day', label: "Show one day's trades", pressed: !weekShown},
    week: {text: 'Week', label: "Show the past week's trades", pressed: weekShown},
  };
  const empty = !weekShown ? null
    : loading || !week ? 'Loading the past week…'
      : !(flows && flows.total) ? 'No trades in the past week'
        : null;
  if (empty) {
    return {
      title: flowTitle('week', previousDay), spans, empty, ariaLabel: null, href: href || null,
      linkLabel: undefined, spoken: null, sizing: null, headers: null, plot: null, legend: null, caption: null,
    };
  }
  // In Week, the range (and any day missing) leads the caption and the spoken label.
  const weekNote = weekShown ? `${week.range}${missingDays(week.missing)}`.trim() : '';
  const lead = weekNote ? `${weekNote}. ` : '';
  const ariaLabel = lead + flowsAccessibilityLabel(flows, day, previousDay);
  const view = {
    title: flowTitle(day, previousDay),
    spans,
    empty: null,
    ariaLabel,
    href: href || null,
    // undefined, not null: ng-attr-* leaves an attribute out only when its value is undefined.
    linkLabel: href ? ariaLabel : undefined,
    spoken: href ? null : ariaLabel,
    sizing: null,
    headers: null,
    plot: null,
    legend: null,
    caption: null,
  };
  if (!(width > 0)) {
    return view;
  }
  // The web has no text-size scaling: fontScale 1. From WIDE up, text, bars and gaps are larger.
  const sizing = fitLabels(flowSizing(width, flows, 1), flows, width, measure);
  return {
    ...view,
    sizing,
    headers: {seller: 'Seller region', buyer: 'Buyer region', style: smallText(sizing)},
    plot: plotView(layoutFlows(flows, width, sizing), sizing),
    legend: {
      style: smallText(sizing),
      items: flows.products.map(band => ({
        slot: band.slot,
        cls: slotClass(band.slot),
        name: bandName(band),
        count: ` ${band.count}`,
      })),
    },
    caption: {text: `${lead}Widths are numbers of trades`, style: smallText(sizing)},
  };
}
